import type { Node as PMNode } from '@milkdown/kit/prose/model';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { EditorView } from '@milkdown/kit/prose/view';
import { $prose } from '@milkdown/kit/utils';

import { isPresent } from './parse';

export const PLATFORM_IMAGE_BLOCK_DIMENSION_SYNC_META = 'platformImageBlockDimensionSync';

const PLATFORM_IMAGE_BLOCK_DIMENSION_PENDING_META = 'platformImageBlockDimensionPending';

const IMAGE_BLOCK = 'image-block';

const platformImageBlockDimensionSyncKey = new PluginKey('platformImageBlockDimensionSync');

export function computePixelDimensions(
  naturalWidth: number,
  naturalHeight: number,
  ratio: number,
): { width: number; height: number } | null {
  if (naturalWidth <= 0 || naturalHeight <= 0 || Number.isNaN(ratio)) return null;

  return {
    width: Math.round(naturalWidth * ratio),
    height: Math.round(naturalHeight * ratio),
  };
}

export function dimensionsMatchAttrs(
  attrs: { width?: number | null; height?: number | null },
  computed: { width: number; height: number },
): boolean {
  const { width, height } = attrs;
  if (!isPresent(width) || !isPresent(height)) return false;

  const widthOk = Math.abs(width - computed.width) <= 1;
  const heightOk = Math.abs(height - computed.height) <= 1;

  return widthOk && heightOk;
}

/**
 * Milkdown ImageViewer sizes via `transformedHeight * ratio`, but Platform markdown stores
 * pixel `width`/`height` (`=WxH`) with ratio defaulting to 1. Derive an effective ratio from
 * stored pixels and apply the same display formula so readonly / markdown-loaded docs match.
 */
export function applyStoredDimensionsToImage(
  img: HTMLImageElement,
  attrs: { width?: number | null; height?: number | null },
  hostMaxWidth?: number,
): boolean {
  const storedW = isPresent(attrs.width) ? Number(attrs.width) : undefined;
  const storedH = isPresent(attrs.height) ? Number(attrs.height) : undefined;
  if (storedW === undefined && storedH === undefined) return false;

  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  if (naturalWidth <= 0 || naturalHeight <= 0) return false;

  const maxWidth = hostMaxWidth ?? img.closest('.milkdown-image-block')?.getBoundingClientRect().width ?? 0;
  const transformedHeight =
    !maxWidth || naturalWidth < maxWidth ? naturalHeight : maxWidth * (naturalHeight / naturalWidth);

  // Ratio from natural pixels so stored =WxH scales down further when the host is narrower than the asset.
  const ratio =
    storedH !== undefined ? storedH / naturalHeight : storedW !== undefined ? storedW / naturalWidth : null;
  if (ratio === null || !Number.isFinite(ratio) || ratio <= 0) return false;

  const displayHeight = transformedHeight * ratio;

  img.dataset.origin = transformedHeight.toFixed(2);
  img.dataset.height = displayHeight.toFixed(2);
  img.style.height = `${displayHeight}px`;

  return true;
}

function getImageFromBlockDom(dom: Node | null): HTMLImageElement | null {
  if (!(dom instanceof HTMLElement)) return null;
  const img = dom.querySelector('img');

  return img instanceof HTMLImageElement ? img : null;
}

function getImageBlockImgForNode(view: EditorView, pos: number, src?: string): HTMLImageElement | null {
  const fromNodeDom = getImageFromBlockDom(view.nodeDOM(pos));
  if (fromNodeDom) return fromNodeDom;

  if (!src) return null;

  for (const el of view.dom.querySelectorAll('.milkdown-image-block img')) {
    if (!(el instanceof HTMLImageElement)) continue;
    const elSrc = el.currentSrc || el.src;
    if (elSrc === src || elSrc.endsWith(src) || el.getAttribute('src') === src) return el;
  }

  return null;
}

function storedDimensionsApplied(
  img: HTMLImageElement,
  attrs: { width?: number | null; height?: number | null },
): boolean {
  const storedH = isPresent(attrs.height) ? Number(attrs.height) : undefined;
  if (storedH === undefined) return true;

  const displayHeight = Number.parseFloat(img.style.height);
  const syncedHeight = Number.parseFloat(img.dataset.height || '0');

  return (
    Number.isFinite(displayHeight) &&
    Math.abs(displayHeight - storedH) <= 1 &&
    Number.isFinite(syncedHeight) &&
    Math.abs(syncedHeight - storedH) <= 1
  );
}

function applyStoredDimensionsInView(view: EditorView): boolean {
  let allApplied = true;

  view.state.doc.descendants((node, pos) => {
    if (node.type.name !== IMAGE_BLOCK) return;
    if (!isPresent(node.attrs.width) && !isPresent(node.attrs.height)) return;

    const img = getImageBlockImgForNode(view, pos, node.attrs.src as string | undefined);
    if (!img || img.naturalWidth <= 0) {
      allApplied = false;

      return;
    }

    applyStoredDimensionsToImage(img, node.attrs);
    if (!storedDimensionsApplied(img, node.attrs)) allApplied = false;
  });

  return allApplied;
}

function scheduleStoredDimensionSync(view: EditorView, isActive: () => boolean, attemptsLeft = 8): void {
  requestAnimationFrame(() => {
    if (!isActive()) return;

    const applied = applyStoredDimensionsInView(view);
    if (!applied && attemptsLeft > 0) scheduleStoredDimensionSync(view, isActive, attemptsLeft - 1);
  });
}

function safeNodeAt(doc: PMNode, pos: number): PMNode | null {
  try {
    return doc.nodeAt(pos);
  } catch {
    return null;
  }
}

function mapPosToOldDoc(pos: number, transactions: readonly import('@milkdown/kit/prose/state').Transaction[]): number {
  return transactions.reduceRight((mappedPos, tr) => tr.mapping.invert().map(mappedPos), pos);
}

function syncDimensionsAtPos(
  view: EditorView,
  pos: number,
  node: PMNode,
  forceRatioSync: boolean,
): { pos: number; attrs: Record<string, unknown> } | null {
  if (node.type.name !== IMAGE_BLOCK) return null;

  const dom = view.nodeDOM(pos);
  const img = getImageFromBlockDom(dom);
  if (!img || img.naturalWidth <= 0) return null;

  const ratio = typeof node.attrs.ratio === 'number' ? node.attrs.ratio : 1;
  const computed = computePixelDimensions(img.naturalWidth, img.naturalHeight, ratio);
  if (!computed) return null;

  // Treat null/undefined as missing so undefined defaults still get synced.
  const hasDimensions = isPresent(node.attrs.width) || isPresent(node.attrs.height);
  if (!forceRatioSync && hasDimensions) return null;
  if (dimensionsMatchAttrs(node.attrs, computed)) return null;

  return {
    pos,
    attrs: {
      ...node.attrs,
      width: computed.width,
      height: computed.height,
    },
  };
}

function collectDimensionUpdates(
  view: EditorView,
  oldDoc: PMNode,
  newDoc: PMNode,
  transactions: readonly import('@milkdown/kit/prose/state').Transaction[],
): Array<{ pos: number; attrs: Record<string, unknown> }> {
  const updates: Array<{ pos: number; attrs: Record<string, unknown> }> = [];

  newDoc.descendants((node, pos) => {
    if (node.type.name !== IMAGE_BLOCK) return;

    const oldPos = mapPosToOldDoc(pos, transactions);
    const oldNode = safeNodeAt(oldDoc, oldPos);
    const ratioChanged = oldNode?.type.name === IMAGE_BLOCK && oldNode.attrs.ratio !== node.attrs.ratio;
    const missingDimensions = !isPresent(node.attrs.width) && !isPresent(node.attrs.height);

    if (!ratioChanged && !missingDimensions) return;

    const update = syncDimensionsAtPos(view, pos, node, ratioChanged);
    if (update) updates.push(update);
  });

  return updates;
}

function applyDimensionUpdatesToTr(
  tr: import('@milkdown/kit/prose/state').Transaction,
  updates: Array<{ pos: number; attrs: Record<string, unknown> }>,
): import('@milkdown/kit/prose/state').Transaction | null {
  if (updates.length === 0) return null;

  const sorted = [...updates].sort((a, b) => b.pos - a.pos);
  for (const { pos, attrs } of sorted) {
    const node = safeNodeAt(tr.doc, pos);
    if (node?.type.name !== IMAGE_BLOCK) continue;

    try {
      tr = tr.setNodeMarkup(pos, undefined, attrs);
    } catch {
      continue;
    }
  }

  if (!tr.docChanged) return null;
  tr.setMeta(PLATFORM_IMAGE_BLOCK_DIMENSION_SYNC_META, true);

  return tr;
}

/**
 * Keeps image-block width/height attrs in sync with ratio changes and image load,
 * bridging stock Milkdown Vue view (ratio-only) to Platform pixel dimensions.
 */
export const platformImageBlockDimensionSync = $prose(() => {
  let viewRef: EditorView | null = null;

  return new Plugin({
    key: platformImageBlockDimensionSyncKey,

    view(view) {
      viewRef = view;
      const isActive = () => viewRef === view;

      const onLoad = (event: Event) => {
        if (!(event.target instanceof HTMLImageElement)) return;
        const img = event.target;
        if (!view.dom.contains(img) || img.naturalWidth <= 0) return;

        try {
          // Schedule via meta-only transaction so updates run in appendTransaction
          // with a consistent doc snapshot (avoids racing direct dispatch on load).
          view.dispatch(view.state.tr.setMeta(PLATFORM_IMAGE_BLOCK_DIMENSION_PENDING_META, img));
        } catch {
          // readonly / teardown — still apply display dims below
        }

        // Run after Milkdown ImageViewer's target `onLoad` (same event turn, later microtasks).
        queueMicrotask(() => scheduleStoredDimensionSync(view, isActive));
      };

      view.dom.addEventListener('load', onLoad, true);

      // Cached images may finish before the listener is attached; retry until styled.
      scheduleStoredDimensionSync(view, isActive);

      return {
        destroy() {
          view.dom.removeEventListener('load', onLoad, true);
          if (viewRef === view) viewRef = null;
        },
      };
    },

    appendTransaction(transactions, oldState, newState) {
      if (transactions.some((txn) => txn.getMeta(PLATFORM_IMAGE_BLOCK_DIMENSION_SYNC_META))) {
        return null;
      }
      if (!viewRef) return null;

      const pendingImageLoads = transactions.some((txn) => txn.getMeta(PLATFORM_IMAGE_BLOCK_DIMENSION_PENDING_META));
      const docChanged = transactions.some((txn) => txn.docChanged);
      if (!docChanged && !pendingImageLoads) return null;

      const updates = collectDimensionUpdates(viewRef, oldState.doc, newState.doc, transactions);
      if (updates.length === 0) {
        // Still re-apply stored pixel dims after ImageViewer may have overwritten styles.
        if (pendingImageLoads && viewRef) {
          const activeView = viewRef;
          queueMicrotask(() => scheduleStoredDimensionSync(activeView, () => viewRef === activeView));
        }

        return null;
      }

      const tr = applyDimensionUpdatesToTr(newState.tr, updates);
      if (viewRef) {
        const activeView = viewRef;
        queueMicrotask(() => scheduleStoredDimensionSync(activeView, () => viewRef === activeView));
      }

      return tr;
    },
  });
});
